import { isBar } from '../lib/places';
import type { PlaceSummary } from '../lib/types';
import { pinColor } from './mapColors';

/**
 * Map pins as icons: a beer mug for a bar, a mug and a burger for a bar that serves food, and for
 * bites a burger (coffee cup for cafes and bakeries, a cone for ice cream). The ring carries the
 * backroad-score color. One definition draws both maps: SVG markup for Leaflet, a canvas image for
 * MapLibre (Path2D takes the same path data, so nothing waits on an image load).
 *
 * Glyph paths are Lucide icons (ISC license), drawn on their 24 px grid.
 */
export type PinGlyph = 'beer' | 'beerFood' | 'burger' | 'coffee' | 'cone';

const GLYPHS: Record<'beer' | 'burger' | 'coffee' | 'cone', string[]> = {
  beer: [
    'M17 11h1a3 3 0 0 1 0 6h-1',
    'M9 12v6',
    'M13 12v6',
    'M14 7.5c-1 0-1.44.5-3 .5s-2-.5-3-.5-1.72.5-2.5.5a2.5 2.5 0 0 1 0-5c.78 0 1.57.5 2.5.5S9.44 2 11 2s2 1.5 3 1.5 1.72-.5 2.5-.5a2.5 2.5 0 0 1 0 5c-.78 0-1.5-.5-2.5-.5Z',
    'M5 8v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8',
  ],
  burger: [
    'M12 16H4a2 2 0 1 1 0-4h16a2 2 0 1 1 0 4h-4.25',
    'M5 12a2 2 0 0 1-2-2 9 7 0 0 1 18 0 2 2 0 0 1-2 2',
    'M5 16a2 2 0 0 0-2 2 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 2 2 0 0 0-2-2q0 0 0 0',
    'm6.67 12 6.13 4.6a2 2 0 0 0 2.8-.4l3.15-4.2',
  ],
  coffee: [
    'M10 2v2',
    'M14 2v2',
    'M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1',
    'M6 2v2',
  ],
  cone: ['m7 11 4.08 10.35a1 1 0 0 0 1.84 0L17 11', 'M17 7A5 5 0 0 0 7 7', 'M17 7a2 2 0 0 1 0 4H7a2 2 0 0 1 0-4'],
};

const INK = '#141716';
const CREAM = '#F3EBD8';
/** The burger beside a bar's mug, tinted like bites so "has food" reads at a glance. */
const FOOD_TINT = '#FFB49C';

export function pinGlyph(p: Pick<PlaceSummary, 'kind' | 'tags'>): PinGlyph {
  if (isBar(p.kind)) return p.tags.includes('Food') ? 'beerFood' : 'beer';
  if (p.kind === 'Cafe' || p.kind === 'Bakery') return 'coffee';
  if (p.kind === 'IceCream') return 'cone';
  return 'burger';
}

/** Size in CSS pixels; a bar with food is a pill holding two glyphs. Compact is for zoomed-out maps. */
export function pinSize(glyph: PinGlyph, selected: boolean, compact = false): { w: number; h: number } {
  const h = selected ? 34 : compact ? 18 : 26;
  return { w: glyph === 'beerFood' ? Math.round(h * 1.6) : h, h };
}

interface Part {
  glyph: keyof typeof GLYPHS;
  color: string;
}

function parts(glyph: PinGlyph): Part[] {
  if (glyph === 'beerFood') return [{ glyph: 'beer', color: CREAM }, { glyph: 'burger', color: FOOD_TINT }];
  return [{ glyph, color: CREAM }];
}

/** Badge geometry in CSS pixels: the rounded outline and where each 24 px glyph sits. */
function layout(glyph: PinGlyph, selected: boolean, compact = false) {
  const { w, h } = pinSize(glyph, selected, compact);
  const ring = selected ? 3 : compact ? 1.75 : 2.25;
  const glyphPx = h * 0.58;
  const scale = glyphPx / 24;
  const ps = parts(glyph);
  const gap = h * 0.08;
  const total = ps.length * glyphPx + (ps.length - 1) * gap;
  const x0 = (w - total) / 2;
  const y0 = (h - glyphPx) / 2;
  return { w, h, ring, scale, parts: ps.map((p, i) => ({ ...p, x: x0 + i * (glyphPx + gap), y: y0 })) };
}

/** SVG markup for a Leaflet divIcon. */
export function pinSvg(glyph: PinGlyph, ringColor: string, selected = false, compact = false): string {
  const l = layout(glyph, selected, compact);
  const r = l.h / 2 - l.ring / 2;
  const body = l.parts
    .map(
      (p) =>
        `<g transform="translate(${p.x.toFixed(2)} ${p.y.toFixed(2)}) scale(${l.scale.toFixed(4)})" fill="none" stroke="${p.color}" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">${GLYPHS[p.glyph]
          .map((d) => `<path d="${d}"/>`)
          .join('')}</g>`,
    )
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${l.w}" height="${l.h}" viewBox="0 0 ${l.w} ${l.h}"><rect x="${l.ring / 2}" y="${l.ring / 2}" width="${l.w - l.ring}" height="${l.h - l.ring}" rx="${r}" fill="${INK}" stroke="${ringColor}" stroke-width="${l.ring}"/>${body}</svg>`;
}

/** The MapLibre image name for a pin; `drawPinImage` turns a name back into pixels. */
export function pinImageName(glyph: PinGlyph, ringColor: string, selected: boolean): string {
  return `pin:${glyph}:${ringColor}:${selected ? 1 : 0}`;
}

/** Draws a pin named by `pinImageName` at the given pixel ratio, or null for a name that isn't one. */
export function drawPinImage(name: string, pixelRatio = 2): ImageData | null {
  const [prefix, glyph, ringColor, sel] = name.split(':');
  if (prefix !== 'pin' || !(glyph in GLYPHS || glyph === 'beerFood')) return null;
  const l = layout(glyph as PinGlyph, sel === '1');
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(l.w * pixelRatio);
  canvas.height = Math.ceil(l.h * pixelRatio);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(pixelRatio, pixelRatio);

  const r = l.h / 2 - l.ring / 2;
  ctx.beginPath();
  ctx.roundRect(l.ring / 2, l.ring / 2, l.w - l.ring, l.h - l.ring, r);
  ctx.fillStyle = INK;
  ctx.fill();
  ctx.lineWidth = l.ring;
  ctx.strokeStyle = ringColor;
  ctx.stroke();

  for (const p of l.parts) {
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.scale(l.scale, l.scale);
    ctx.lineWidth = 2.1;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = p.color;
    for (const d of GLYPHS[p.glyph]) ctx.stroke(new Path2D(d));
    ctx.restore();
  }
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/** Everything a map needs for one place's pin. */
export function pinFor(p: Pick<PlaceSummary, 'kind' | 'tags' | 'isCurated' | 'backroadScore'>, selected: boolean) {
  const glyph = pinGlyph(p);
  const ring = selected ? '#FFFFFF' : pinColor(p);
  return { glyph, ring, size: pinSize(glyph, selected), image: pinImageName(glyph, ring, selected) };
}
